import { AxiosError, AxiosResponse } from "axios";
import api from "../axios";
import {
  AttachPropertyRequest,
  AttachPropertyResponse,
  ConvertCurrencyRequest,
  ConvertCurrencyResponse,
  GenerateAccountStatementRequest,
  GenerateAccountStatementResponse,
  GetExistingLoansResponse,
  GetRentFinanceResponse,
  InitiateCreditCheckRequest,
  InitiateCreditCheckResponse,
  MortDocumentUploadResponse,
  PrequalifyRequest,
  PrequalifyResponse,
  SubmitExistingLoansRequest,
  SubmitExistingLoansResponse,
  SubmitMortgageResponse,
  UploadDocExtMortageReq,
  ValidateAccountRequest,
  ValidateAccountResponse,
  ValidateAccountVerifyOtpRequest,
  ValidateAccountVerifyOtpResponse,
  VerifyBvnSendOtpRequest,
  VerifyBvnSendOtpResponse,
  VerifyBvnVerifyOtpRequest,
  VerifyBvnVerifyOtpResponse,
} from "../types/mortgage";

export const mortgagePrequalifyApi = async (req: PrequalifyRequest) => {
  const willPrequalifyForMortgage = new Promise<PrequalifyResponse>(
    (resolve) => {
      api
        .post("/api/MortgageApplication/pre-qualify", req)
        .then((response: AxiosResponse<PrequalifyResponse["data"]>) => {
          resolve({ data: response.data });
        })
        .catch((err: AxiosError | any) => {
          resolve({ error: err });
        });
    }
  );

  return willPrequalifyForMortgage;
};

export const mortgageAttachPropertyApi = async (req: AttachPropertyRequest) => {
  const willAttachProperty = new Promise<AttachPropertyResponse>((resolve) => {
    api
      .post("/api/MortgageApplication/attach-property", req)
      .then((response: AxiosResponse<AttachPropertyResponse["data"]>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError | any) => {
        resolve({ error: err });
      });
  });

  return willAttachProperty;
};

export const submitMortgageApplication = async (applicationId: number) => {
  const willSubmitMortgage = new Promise<SubmitMortgageResponse>((resolve) => {
    api
      .post(`/api/MortgageApplication/submit/${applicationId}`)
      .then((response: AxiosResponse<SubmitMortgageResponse["data"]>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError | any) => {
        resolve({ error: err });
      });
  });

  return willSubmitMortgage;
};

export const mortgageDocumentUploadApi = async (
  req: UploadDocExtMortageReq
) => {
  const formData = new FormData();
  formData.append("MortgageApplicationId", String(req.MortgageApplicationId));
  req.DocumentTypes.forEach((type) =>
    formData.append("DocumentTypes", String(type))
  );
  req.Files.forEach((file) => formData.append("Files", file));
  const willUploadDocuments = new Promise<MortDocumentUploadResponse>(
    (resolve, reject) => {
      api
        .post("/api/MortgageApplication/upload-documents", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response: AxiosResponse<MortDocumentUploadResponse["data"]>) => {
          resolve({ data: response.data });
        })
        .catch((err: AxiosError | any) => {
          reject({ error: err });
        });
    }
  );

  return willUploadDocuments;
};

export const getRentFinanceUrlApi = () => {
  const willGetUrl = new Promise<GetRentFinanceResponse>((resolve) => {
    api
      .get("/api/MortgageApplication/rent-finance")
      .then((response: AxiosResponse<GetRentFinanceResponse["data"]>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError) => {
        resolve({ error: err });
      });
  });
  return willGetUrl;
};

export const mortgageConvertCurrencyApi = async (
  req: ConvertCurrencyRequest
) => {
  const willConvertCurrency = new Promise<ConvertCurrencyResponse>(
    (resolve) => {
      api
        .post("/api/MortgageApplication/fx-preview", req)
        .then((response: AxiosResponse<ConvertCurrencyResponse["data"]>) => {
          resolve({ data: response.data });
        })
        .catch((err: AxiosError | any) => {
          resolve({ error: err });
        });
    }
  );

  return willConvertCurrency;
};

export const mortgageGetAccountNameApi = async (
  request: ValidateAccountRequest
) => {
  const willValidateAccount = new Promise<ValidateAccountResponse>(
    (resolve) => {
      api
        .post("/api/MortgageApplication/validate-account", request)
        .then((response: AxiosResponse<ValidateAccountResponse["data"]>) => {
          resolve({ data: response.data });
        })
        .catch((err: AxiosError | any) => {
          resolve({ error: err });
        });
    }
  );

  return willValidateAccount;
};

export const mortgageValidateAccVerifyOtpApi = async (
  request: ValidateAccountVerifyOtpRequest
) => {
  const willValidateAccount = new Promise<ValidateAccountVerifyOtpResponse>(
    (resolve) => {
      api
        .post("/api/MortgageApplication/validate-account/verify-otp", request)
        .then(
          (
            response: AxiosResponse<ValidateAccountVerifyOtpResponse["data"]>
          ) => {
            resolve({ data: response.data });
          }
        )
        .catch((err: AxiosError | any) => {
          resolve({ error: err });
        });
    }
  );

  return willValidateAccount;
};

export const verifyBvnSendOtp = async (request: VerifyBvnSendOtpRequest) => {
  const willSendOtpForBvnVerification = new Promise<VerifyBvnSendOtpResponse>(
    (resolve) => {
      api
        .post("/api/MortgageApplication/verify-bvn/send-otp", request)
        .then((response: AxiosResponse<VerifyBvnSendOtpResponse["data"]>) => {
          resolve({ data: response.data });
        })
        .catch((err: AxiosError | any) => {
          resolve({ error: err });
        });
    }
  );
  return willSendOtpForBvnVerification;
};

export const verifyBvnVerifyOtp = async (
  request: VerifyBvnVerifyOtpRequest
) => {
  const willVerifyOtpForBvnVerification =
    new Promise<VerifyBvnVerifyOtpResponse>((resolve) => {
      api
        .post("/api/MortgageApplication/verify-bvn/verify-otp", request)
        .then((response: AxiosResponse<VerifyBvnVerifyOtpResponse["data"]>) => {
          resolve({ data: response.data });
        })
        .catch((err: AxiosError | any) => {
          resolve({ error: err });
        });
    });
  return willVerifyOtpForBvnVerification;
};

export const getExistingLoans = async (bvn: string | null) => {
  const willGetExistingLoans = new Promise<GetExistingLoansResponse>(
    (resolve) => {
      api
        .get(`/api/MortgageApplication/credit-check-test/${bvn}`)
        .then((response: AxiosResponse<GetExistingLoansResponse["data"]>) => {
          resolve({ data: response.data });
        })
        .catch((err: AxiosError | any) => {
          resolve({ error: err });
        });
    }
  );
  return willGetExistingLoans;
};

export const initiateCreditCheck = async (
  request: InitiateCreditCheckRequest
) => {
  const willInitiateCreditCheck = new Promise<InitiateCreditCheckResponse>(
    (resolve) => {
      api
        .post("/api/MortgageApplication/initiate-credit-check", request)
        .then(
          (response: AxiosResponse<InitiateCreditCheckResponse["data"]>) => {
            resolve({ data: response.data });
          }
        )
        .catch((err: AxiosError | any) => {
          resolve({ error: err });
        });
    }
  );
  return willInitiateCreditCheck;
};

export const submitExistingLoans = async (
  request: SubmitExistingLoansRequest
) => {
  const willSubmitExistingLoans = new Promise<SubmitExistingLoansResponse>(
    (resolve) => {
      api
        .post("/api/MortgageApplication/submit-existing-loans", request)
        .then(
          (response: AxiosResponse<SubmitExistingLoansResponse["data"]>) => {
            resolve({ data: response.data });
          }
        )
        .catch((err: AxiosError | any) => {
          resolve({ error: err });
        });
    }
  );
  return willSubmitExistingLoans;
};

export const generateAccountStatement = async (
  request: GenerateAccountStatementRequest,
  mortgageApplicationId: number
) => {
  const willGenerateStatement = new Promise<GenerateAccountStatementResponse>(
    (resolve) => {
      api 
        .post(
          `/api/MortgageApplication/generate-statement/${mortgageApplicationId}`,
          request
        )
        .then(
          (
            response: AxiosResponse<GenerateAccountStatementResponse["data"]>
          ) => {
            resolve({ data: response.data });
          }
        )
        .catch((err: AxiosError | any) => {
          resolve({ error: err });
        });
    }
  );
  return willGenerateStatement;
};
